import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _StyledEmptyState;
const _excluded = ["children", "renderEmptyState", "loading"];
import React, { useState, useEffect, forwardRef, Children } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Spinner from '../Spinner';
import Accordion from '../Accordion';
import { useMergeRefs } from '../hooks';
import { isFunction } from '../helper';
import { ScrollbarStyle } from '../StyleBase';
const displayName = 'DropdownContainer';

/**
 * Fallback for Node enviroments.
 */
global.MutationObserver = global.MutationObserver || class {
  observe() {}
  disconnect() {}
};
const Root = styled.div.withConfig({
  displayName: "DropdownContainer__Root",
  componentId: "sc-d3i33p-0"
})(["", " overflow-y:auto;flex:1;", "{text-transform:uppercase;color:var(--neutral-text-low-emphasis);font:var(--text-xs-bold);padding:var(--size-spacing-02) var(--size-spacing-04);}"], ScrollbarStyle, Accordion.HeadStyle);
const StyledEmptyState = styled.div.withConfig({
  displayName: "DropdownContainer__StyledEmptyState",
  componentId: "sc-d3i33p-1"
})(["", ""], _ref => {
  let {
    $isCustomContent
  } = _ref;
  return !$isCustomContent && css(["padding:var(--size-spacing-04);font:var(--text-sm-bold);text-align:center;"]);
});
const EmptyState = _ref2 => {
  let {
    children
  } = _ref2;
  if (!children) return null;
  const content = isFunction(children) ? children() : children;
  return /*#__PURE__*/React.createElement(StyledEmptyState, {
    $isCustomContent: isFunction(content)
  }, content);
};
const DropdownContainer = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
      children,
      renderEmptyState,
      loading
    } = _ref3,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded);
  const [element, setElement] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const mergedRef = useMergeRefs([setElement, ref]);

  /**
   * In the `Autocomplete` the children aren't removed from the DOM in some situations,
   * but only hidden.
   *
   * Because of this the use of `MutationObserver` was necessary.
   *
   * For more informations: https://developer.mozilla.org/docs/Web/API/MutationObserver
   */
  useEffect(() => {
    if (!element) return;
    const mutationObserver = new global.MutationObserver(() => {
      const visibleChildren = element.querySelectorAll(":scope > *:not([hidden]):not(".concat(StyledEmptyState.toString(), ")"));
      setIsEmpty(visibleChildren.length === 0);
    });
    mutationObserver.observe(element, {
      childList: true
    });
    return () => mutationObserver.disconnect();
  }, [element]);
  const isEmptyState = isEmpty || !Children.count(children) && !loading;
  return /*#__PURE__*/React.createElement(Root, _extends({
    "aria-busy": loading
  }, rest, {
    ref: mergedRef
  }), !loading && children, loading && (_StyledEmptyState || (_StyledEmptyState = /*#__PURE__*/React.createElement(StyledEmptyState, null, /*#__PURE__*/React.createElement(Spinner, null)))), isEmptyState && /*#__PURE__*/React.createElement(EmptyState, null, renderEmptyState));
});
DropdownContainer.displayName = displayName;
DropdownContainer.Style = Root;
DropdownContainer.defaultProps = {
  loading: false
};
process.env.NODE_ENV !== "production" ? DropdownContainer.propTypes = {
  /**
   * Sets the children.
   */
  children: PropTypes.node,
  /**
   * Sets the text to be displayed when content not exists or is hidden.
   * If you want to customize the renderization of this property, you must pass a component instead.
   *
   * @example
   * renderEmptyState="No items were found"
   * renderEmptyState={() => <MyComponent>No items were found</MyComponent>}
   */
  renderEmptyState: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Sets loading appearance.
   */
  loading: PropTypes.bool
} : void 0;
export default DropdownContainer;